import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const Sleep = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'sleep'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container sleep-page">
      <h1 className="page-title">Sleep and Restful Recovery</h1>

      {/* Include the image */}
      <img
        src="/images/wf-sleep2.webp"
        alt="Sleep Illustration"
        className="page-image"
      />

      <p className="page-intro">
        Welcome to the Sleep Health Benefits section of Wholeness Forward. Sleep is essential to our physical and mental well-being, yet it’s often neglected in our busy lives.
      </p>

      <p className="page-text">
        Sleep is crucial for recovery, memory consolidation, and overall health. A good night’s sleep allows the body and brain to heal, process information, and recharge.
      </p>

      <hr className="page-divider" />

      <h2 className="section-title">Key Benefits of Sleep</h2>
      <p className="section-description">
        Here are just a few of the many health benefits of proper sleep:
      </p>
      <ul className="content-list">
        <li><strong>Improved Cognitive Function:</strong> Sleep enhances memory, concentration, and problem-solving skills.</li>
        <li><strong>Physical Recovery:</strong> Promotes muscle repair, immune system function, and overall physical recovery.</li>
        <li><strong>Mental Health:</strong> Supports emotional stability and reduces stress, anxiety, and depression.</li>
        <li><strong>Metabolic Health:</strong> Helps regulate appetite and metabolism, reducing the risk of obesity and diabetes.</li>
        <li><strong>Heart Health:</strong> Lowers the risk of heart disease and stroke by maintaining blood pressure and reducing inflammation.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="section-title">Practical Tips for Better Sleep</h2>
      <p className="section-description">Use these actionable tips to improve your sleep quality:</p>
      <ul className="content-list">
        <li>Maintain a consistent sleep schedule, even on weekends.</li>
        <li>Create a relaxing bedtime routine to signal to your body that it’s time to wind down.</li>
        <li>Optimize your sleep environment by keeping it cool, dark, and quiet.</li>
        <li>Limit screen time and exposure to blue light before bed.</li>
        <li>Avoid caffeine, heavy meals, and alcohol close to bedtime.</li>
        <li>Engage in regular physical activity to improve sleep quality.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="section-title">Curated Video Collection</h2>
      <p className="section-description">Learn more about the importance of sleep and how to improve your sleep quality with these handpicked videos:</p>

      {loading ? (
        <p>Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p>No videos found for this category.</p>
          )}
        </div>
      )}


      <hr className="page-divider" />

      <h2 className="section-title">Popular Apps for Improving Sleep</h2>
      <p className="section-description">These apps can help you track your sleep and build better habits:</p>

      <h3 className="content-subtitle">Calm</h3>
      <p>Calm offers sleep stories, meditation, and breathing exercises to help you unwind and improve your sleep quality.</p>

      <h3 className="content-subtitle">Headspace</h3>
      <p>Headspace provides guided meditations and sleepcasts that can help you fall asleep more easily and wake up refreshed.</p>

      <h3 className="content-subtitle">Sleep Cycle</h3>
      <p>Sleep Cycle tracks your sleep patterns and wakes you up at the optimal time to help you feel rested and energized.</p>

      <hr className="page-divider" />

      <h2 className="section-title">Join the Sleep Health Forum</h2>
      <p className="section-description">
        Have any tips or insights on sleep health? Join our community forum to connect with others who are passionate about getting better rest.
      </p>
      <div className="mini_void"></div>
      <a href="/forum/Sleep" className="forum-link">
        Go to Sleep Forum
      </a>
    </div>
  );
};

export default Sleep;
